// ─────────────────────────────────────────────
//  DoctorReleaseMenu — the "Extra doctor ▾" menu on DoctorsNeededBox.
//
//  ── IT SAYS HOW MANY, NEVER WHOM ─────────────────────────────────────────
//  `releasable` is a COUNT: available now minus the peak of the rest of the OR
//  day. The engine has no opinion about WHICH doctor should go — that depends on
//  who has been here longest, who is on tomorrow, who has clinic, and a dozen
//  things no roster field holds. So this menu states the number, lists everyone
//  who is actually free, and lets a human pick. Pre-selecting a name, or sorting
//  by "most releasable", would dress a scheduling judgement up as a computation.
//
//  ── IT REUSES DEPARTURE, IT DOES NOT REIMPLEMENT IT ──────────────────────
//  Releasing a doctor IS departing them, and departure already enforces the
//  rules that matter (every active room reassigned, the AIC hands off first).
//  Each row therefore renders the page's existing DepartureFlow through
//  `renderDepart` — the identical control the MD Call rail and the Daily List
//  use. No new write path exists in this file, and there must never be one:
//  a second way to send a doctor home is a second set of rules to keep right.
//
//  ── A ROW THAT CANNOT ACT SAYS SO ────────────────────────────────────────
//  `renderDepart` returns null for anyone with no roster identity — a locum the
//  sheet invented under a synthetic `mdcall:` id, or a name that matched no
//  staff record. Those rows stay VISIBLE and name the obstacle. Hiding them
//  would make the list disagree with the count above it, and a manager counting
//  four names under a heading that says five will trust neither again.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

const hhmm = (min) => {
  if (!Number.isFinite(min)) return null;
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  const ampm = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${m ? `:${String(m).padStart(2, "0")}` : ""}${ampm}`;
};

function Row({
  entry,
  renderDepart,
  locked = false,
  lockNote = "",
  destinations = [],
  expanded = false,
  onExpand,
  onTransfer,
  busy = false,
}) {
  const depart = locked || typeof renderDepart !== "function" ? null : renderDepart(entry);
  const leaves = hhmm(entry.leaveMin);
  // ⚠️ TRANSFER RIDES ON THE SAME IDENTITY DEPART DOES. `renderDepart` returns
  // null for anyone with no roster record, and a transfer writes
  // `providerId: entry.key` — so offering Transfer where Depart is refused
  // would post a transfer for a person the roster cannot name.
  const canTransfer = Boolean(depart) && destinations.length > 0 && typeof onTransfer === "function";
  return (
    <li style={s.row} data-testid={`release-row-${entry.key}`} data-locked={String(locked || !depart)}>
      <div style={s.rowMain}>
        <span style={s.name}>{entry.name}</span>
        {depart ? (
          <span style={s.action}>{depart}</span>
        ) : (
          // Never an empty cell where a control belongs. See the header note.
          <span style={s.noAction} data-testid={`release-blocked-${entry.key}`}>
            {lockNote || "no roster record — depart from the Daily List"}
          </span>
        )}
      </div>
      {leaves && <div style={s.rowNote}>scheduled out {leaves}</div>}

      {/* ── TRANSFER ────────────────────────────────────────────────────────
          Two actions, two verbs, two consequences: Depart sends a doctor HOME,
          Transfer sends them to another campus and they are still working.
          Naming both for the consequence is why they cannot share one control.

          Collapsed by default — a 179px rail cannot hold two buttons and a name
          on one line, and the name is the only thing that would shrink. */}
      {canTransfer && !expanded && (
        <button
          type="button"
          style={s.transferBtn}
          onClick={() => onExpand(entry.key)}
          data-testid={`release-transfer-${entry.key}`}
        >
          → Transfer
        </button>
      )}
      {canTransfer && expanded && (
        <div style={s.destList} data-testid={`release-dests-${entry.key}`}>
          <div style={s.destLabel}>Send {entry.name} to:</div>
          {destinations.map((d) => (
            <button
              key={d.id}
              type="button"
              style={s.destBtn}
              disabled={busy}
              onClick={() => onTransfer(entry, d)}
              data-testid={`release-dest-${entry.key}-${d.id}`}
            >
              {d.label}
            </button>
          ))}
          <button type="button" style={s.destCancel} onClick={() => onExpand(null)} disabled={busy}>
            Cancel
          </button>
        </div>
      )}
    </li>
  );
}

export default function DoctorReleaseMenu({
  open = false,
  onClose,
  releasable = 0,
  // now.doctorList — free to supervise at this minute.
  doctors = [],
  // now.soloList — present, but locked in a solo case and therefore not free.
  solo = [],
  // "back to 4 MD at 12:00" — the reason a surplus is not releasable.
  holdNote = "",
  renderDepart = null,
  // ⚠️ ALREADY FILTERED BY THE CALLER to campuses this account can actually
  // write to. `otherFacilities()` is a hardcoded tenant list that does NOT
  // consult `user.facilityIds` (TimelineChartPage:1562 documents the same trap
  // costing two permission-denied listeners), so an unfiltered list here would
  // offer a transfer the rules then refuse — a control that looks live and is
  // not. Empty list = no Transfer offered at all.
  destinations = [],
  onTransfer = null,
  transferBusyKey = null,
}) {
  const [expandedKey, setExpandedKey] = React.useState(null);

  // Collapse any open destination list when the panel closes, or the next open
  // starts mid-transfer on a doctor the manager was no longer looking at.
  React.useEffect(() => {
    if (!open) setExpandedKey(null);
  }, [open]);

  if (!open) return null;

  const canGo = Number.isFinite(releasable) ? Math.max(0, releasable) : 0;
  const list = Array.isArray(doctors) ? doctors : [];
  const soloList = Array.isArray(solo) ? solo : [];
  const dests = Array.isArray(destinations) ? destinations : [];

  return (
    // ⚠️ NOT role="menu", DELIBERATELY — and this reverses the original plan.
    //
    // The plan said to reuse benchAssign/ChoiceMenu with role=menu and roving
    // arrow keys. ChoiceMenu turns out to be 32 lines of `role="menuitem"`
    // buttons whose arrow keys live in BenchAssignFlow (:432), and every row is
    // ONE action with one onPick. These rows are not that shape: each embeds
    // DepartureFlow's own trigger, a foreign button this file cannot annotate
    // as a menuitem, alongside a name and a scheduled-out line.
    //
    // A role="menu" whose items are not menuitems is a promise the keyboard
    // cannot keep — a screen reader announces a menu and then the arrow keys do
    // nothing. That is the same false-contract bug as reusing the shared Alert
    // for static text. A labelled group of rows, each holding a real <button>,
    // is what this actually is: Tab reaches every Depart control in order, which
    // is the correct interaction for a list of buttons and needs no roving
    // handler. Escape still closes, because the panel is a disclosure.
    // Menu container with onKeyDown — the keyboard path, not a mouse-only trap.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      style={s.sheet}
      role="group"
      aria-label="Extra doctor"
      onKeyDown={(e) => {
        if (e.key === "Escape" && typeof onClose === "function") {
          e.stopPropagation();
          onClose();
        }
      }}
      data-testid="doctor-release-menu"
    >
      <div style={s.head}>
        <span style={s.headCount} data-testid="release-count">
          {canGo > 0 ? `${canGo} can go` : "None to release"}
        </span>
        <button
          type="button"
          onClick={onClose}
          style={s.close}
          // ⚠️ SCOPED LABEL. A bare "✕" here collided with the case modal's own
          // close button in tests, and a page-wide match hit the wrong control.
          aria-label="Close extra doctor menu"
          data-testid="release-close"
        >
          ✕
        </button>
      </div>

      {/* WHY the surplus is not releasable. Without this the count reads as a
          refusal; with it, it is an explanation a manager can plan around. */}
      {canGo === 0 && holdNote && (
        <div style={s.holdNote} data-testid="release-hold-note">
          {holdNote}
        </div>
      )}

      {/* The count is the engine's; the choice is the manager's. */}
      {canGo > 0 && (
        <div style={s.pickNote}>Pick who goes — the board only knows how many.</div>
      )}

      {list.length === 0 && soloList.length === 0 ? (
        <div style={s.empty}>No doctors are on the board right now.</div>
      ) : (
        <ul style={s.list}>
          {list.map((entry) => (
            <Row
              key={entry.key}
              entry={entry}
              renderDepart={renderDepart}
              destinations={dests}
              expanded={expandedKey === entry.key}
              onExpand={setExpandedKey}
              onTransfer={onTransfer}
              busy={transferBusyKey === entry.key}
            />
          ))}
          {soloList.map((entry) => (
            <Row
              key={entry.key}
              entry={entry}
              renderDepart={renderDepart}
              // ⚠️ NO TRANSFER EITHER. A doctor mid solo case cannot be sent to
              // another campus for the same reason they cannot be sent home:
              // there is a patient in the room.
              locked
              // A solo doctor is not "unavailable" in general — they are in a
              // case. Saying which is the difference between a rule and a fault.
              lockNote="in a solo case"
            />
          ))}
        </ul>
      )}
    </div>
  );
}

const s = {
  sheet: {
    marginTop: 6,
    padding: "7px 8px 8px",
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
  },
  head: { display: "flex", alignItems: "center", gap: 6 },
  headCount: { fontSize: 11, fontWeight: font.weight.bold, color: color.text.primary },
  close: {
    marginLeft: "auto",
    // 44px on a phone: this rail stacks, and the menu must be dismissable there.
    minWidth: 44,
    minHeight: 44,
    fontSize: 12,
    lineHeight: 1,
    color: color.text.secondary,
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },
  holdNote: { fontSize: 9.5, lineHeight: 1.35, color: color.text.secondary, marginTop: 2 },
  pickNote: { fontSize: 9.5, lineHeight: 1.35, color: color.text.muted, marginTop: 2 },
  list: { listStyle: "none", margin: "6px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 4 },
  row: {
    padding: "3px 5px",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
  },
  rowMain: { display: "flex", alignItems: "center", gap: 5 },
  // ⚠️ THE NAME IS THE ONLY SHRINKABLE THING in a 179px rail, so it gets the
  // flex and the truncation; the control keeps its intrinsic width.
  name: { fontSize: 10.5, fontWeight: font.weight.bold, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  action: { flexShrink: 0 },
  noAction: { fontSize: 8.5, color: color.text.muted, flexShrink: 0, lineHeight: 1.3, textAlign: "right", maxWidth: 92 },
  rowNote: { fontSize: 8.5, color: color.text.muted, marginTop: 1 },

  // 44px minimum on every one of these: this rail stacks on a phone, and a
  // transfer moves a real person between hospitals — not a control to fat-finger.
  transferBtn: {
    marginTop: 3,
    width: "100%",
    minHeight: 44,
    fontSize: 9.5,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    background: "transparent",
    border: `1px dashed ${color.border.default}`,
    borderRadius: radius.sm,
    cursor: "pointer",
  },
  destList: { marginTop: 3, display: "flex", flexDirection: "column", gap: 3 },
  destLabel: { fontSize: 8.5, color: color.text.muted, lineHeight: 1.3 },
  destBtn: {
    width: "100%",
    minHeight: 44,
    fontSize: 10,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    background: color.surface.card,
    border: `1px solid ${color.accent.closing}`,
    borderRadius: radius.sm,
    cursor: "pointer",
  },
  destCancel: {
    width: "100%",
    minHeight: 44,
    fontSize: 9.5,
    color: color.text.muted,
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },
  empty: { fontSize: 10, color: color.text.secondary, marginTop: 4, lineHeight: 1.35 },
};
